import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RQAPICall, RQAPICallWithData } from 'store/APIClient';
import { Dispatch, SetStateAction } from 'react';
import { isCurrentlyOnPublicDashboard } from 'helpers/dashboardHelpers';
import { CommentT } from '../Comment';

export const useGetComments = (chartKey: number, dashboardKey?: number) => {
    const isOnPublicDashboard = isCurrentlyOnPublicDashboard();
    return useQuery({
        queryKey: ['chart', chartKey, 'comments'],

        queryFn: async () => {
            const response = await RQAPICallWithData<CommentT[]>({
                endpoint: `explore/dashboard/${dashboardKey}/chart/${chartKey}/comments`,
                method: 'GET',
            });
            return response.content.data;
        },

        enabled: !!chartKey && !isOnPublicDashboard,
    });
};

const createComment = async (
    content: string,
    chartKey: number,
    dashboardKey?: number,
): Promise<CommentT> => {
    const response = await RQAPICallWithData<CommentT>({
        endpoint: `explore/dashboard/${dashboardKey}/chart/${chartKey}/comments`,
        method: 'POST',
        body: {
            content,
        },
    });
    return response.content.data;
};

export const useCreateComment = (
    setComments: Dispatch<SetStateAction<CommentT[]>>,
    chartKey: number,
    dashboardKey?: number,
) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (content: string) =>
            createComment(content, chartKey, dashboardKey),

        onSuccess: (comment) => {
            setComments((prevState) => [...prevState, comment]);
            queryClient.setQueryData(
                ['chart', chartKey, 'comments'],
                (oldComments: CommentT[]) => [...oldComments, comment],
            );
        },
    });
};

const deleteComment = async (
    commentId: number,
    chartKey: number,
    dashboardKey?: number,
) => {
    const response = await RQAPICall({
        endpoint: `explore/dashboard/${dashboardKey}/chart/${chartKey}/comment/${commentId}`,
        method: 'DELETE',
    });
    return response;
};
export const useDeleteComment = (
    setComments: Dispatch<SetStateAction<CommentT[]>>,
    chartKey: number,
    dashboardKey?: number,
) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (commentId: number) =>
            deleteComment(commentId, chartKey, dashboardKey),

        onSuccess: (data, id) => {
            setComments((prevState) =>
                prevState.filter((com) => com.id !== id),
            );
            queryClient.setQueryData(
                ['chart', chartKey, 'comments'],
                (oldComments: CommentT[]) =>
                    oldComments.filter((oldComment) => oldComment.id !== id),
            );
        },
    });
};
